import React, { useCallback, useState, useRef } from 'react';
import { TextInput } from '../TextInput/TextInput';
import { IconClose, IconSearch, theme, useLocalizationContext } from '@contrast-security-inc/design-system-foundations';
import { useUniqueId } from '../utils/useUniqueID';
import { css, cx } from '@emotion/css';
import * as messages from './translations';
const searchIconWrapperStyles = css `
  display: flex;
  margin-left: 12px;
  padding: 0;

  + input[type="search"],
  + input[disabled] {
    padding-left: 8px;
  }

  /* stylelint-disable-next-line selector-no-vendor-prefix */
  + input[type="search"]:focus::placeholder,
  + input[type="search"]:focus::-webkit-input-placeholder { /* stylelint-disable-line selector-no-vendor-prefix */
    opacity: 0;
  }

  + input[type="search"]::-webkit-search-decoration,
  + input[type="search"]::-webkit-search-cancel-button,
  + input[type="search"]::-webkit-search-results-button,
  + input[type="search"]::-webkit-search-results-decoration {
    appearance: none;
  }
`;
const clearSearchStyles = css `
  align-items: center;
  align-self: center;
  background: ${theme.color.white};
  border-radius: 6px;
  border: 1px solid ${theme.color.transparent};
  cursor: pointer;
  display: flex;
  justify-content: center;
  margin-right: 12px;
  padding: 8px 8px 8px 0;

  &:focus-visible {
    border-color: ${theme.color.turquoise70};
    outline: none;
    padding-left: 8px;
  }
`;
export const SearchInput = React.forwardRef(({ className, debounce = 300, id, isDisabled, onChange, placeholder, testId, value, ...rest }, ref) => {
    const [inputValue, setInputValue] = useState(value);
    const inputContainerRef = useRef(null);
    const { formatMessage } = useLocalizationContext();
    const uniqueId = useUniqueId(id);
    const placeholderText = placeholder ?? formatMessage(messages, 'placeholder');
    const ariaLabel = placeholderText.trim() ? placeholderText : formatMessage(messages, 'placeholder');
    let debounceSetTimeout;
    const debounceOnChange = (event, newValue) => {
        clearTimeout(debounceSetTimeout);
        debounceSetTimeout = setTimeout(() => {
            onChange(event, newValue);
        }, debounce);
    };
    const onTextChange = useCallback((event) => {
        if (!isDisabled) {
            setInputValue(event.target.value);
            debounceOnChange(event, event.target.value);
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDisabled]);
    return (React.createElement(TextInput, { ...rest, "aria-label": ariaLabel, className: cx(className, 'ds-search-input'), id: uniqueId, isDisabled: isDisabled, label: "", onChange: onTextChange, placeholder: placeholderText, containerRef: inputContainerRef, ref: ref, testId: testId, type: "search", value: inputValue, startAdornment: React.createElement("div", { className: searchIconWrapperStyles },
            React.createElement(IconSearch, { size: "md", color: "neutral100" })), endAdornment: !isDisabled && inputValue ? (React.createElement("button", { "aria-label": formatMessage(messages, 'delete'), className: clearSearchStyles, onClick: () => {
                setInputValue('');
                if (inputContainerRef.current) {
                    const searchInput = inputContainerRef.current.querySelector('input[type="search"]');
                    const prototypeValueSetter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(searchInput), 'value')?.set;
                    prototypeValueSetter?.call(searchInput, '');
                    searchInput?.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }, type: "button" },
            React.createElement(IconClose, { size: "sm", color: "black" }))) : undefined }));
});
SearchInput.displayName = 'SearchInput';
